import axios, { AxiosRequestConfig } from "axios";
import * as Sentry from "@sentry/nextjs";

// ----------------------------------------------------------------------

// Extend AxiosRequestConfig to include metadata for tracking
declare module "axios" {
  export interface AxiosRequestConfig {
    metadata?: {
      startTime: number;
    };
  }
}

export const axiosFetcher = async (url: string, serializedParams?: any) => {
  if (serializedParams) {
    const params = JSON.parse(serializedParams);
    let requestParams: AxiosRequestConfig = { params };

    const response = await axiosInstance.get(url, requestParams);
    if (!response?.data) {
      const error = {
        info: "",
        status: 0,
      };

      error.info = (response as unknown as { message: string })?.message;
      error.status = response?.status;
      throw error;
    }

    return response.data;
  } else {
    const response = await axiosInstance.get(url);
    return response.data;
  }
};

const axiosInstance = axios.create({
  baseURL: process.env.TL_API || "",
});

[axiosInstance].forEach((item) => {
  // Request interceptor to track multipart/form-data uploads
  item.interceptors.request.use(
    (config) => {
      const isMultipartUpload =
        config.headers?.["Content-Type"] === "multipart/form-data";

      if (isMultipartUpload) {
        // Store start time for this request
        config.metadata = { startTime: performance.now() };

        Sentry.addBreadcrumb({
          category: "api-request",
          message: "Multipart upload request started",
          level: "info",
          data: {
            url: config.url,
            method: config.method,
            baseURL: config.baseURL,
          },
        });
      }

      config.headers = {
        "Content-MD5": "application/json",
        Accept: "application/json",
        ...config.headers,
      };
      return config;
    },
    (error) => {
      Sentry.captureException(error, {
        tags: {
          interceptor: "request",
        },
      });
      return Promise.reject(error);
    }
  );

  item.interceptors.response.use(
    (response) => {
      // Track successful multipart upload completion
      const isMultipartUpload =
        response.config.headers?.["Content-Type"] === "multipart/form-data";

      if (isMultipartUpload && response.config.metadata?.startTime) {
        const duration = performance.now() - response.config.metadata.startTime;

        Sentry.addBreadcrumb({
          category: "api-response",
          message: "Multipart upload completed successfully",
          level: "info",
          data: {
            url: response.config.url,
            status: response.status,
            durationMs: duration.toFixed(2),
            durationSec: (duration / 1000).toFixed(2),
          },
        });
      }

      return response;
    },
    (error) => {
      // Track multipart upload errors
      const isMultipartUpload =
        error.config?.headers?.["Content-Type"] === "multipart/form-data";

      if (isMultipartUpload) {
        const duration = error.config?.metadata?.startTime
          ? performance.now() - error.config.metadata.startTime
          : 0;

        Sentry.captureException(error, {
          tags: {
            interceptor: "response-error",
            uploadType: "multipart",
          },
          extra: {
            url: error.config?.url,
            method: error.config?.method,
            status: error.response?.status,
            statusText: error.response?.statusText,
            durationMs: duration.toFixed(2),
            errorMessage: error.message,
          },
        });
      }

      // Do something with response error

      if (!error.response) return error;

      switch (error.response.status) {
        case 401: {
          const payload = error.response
            ? {
              status: 401,
              data: error.response.messsage || error.response,
            }
            : {
              status: 401,
              data: "User not Authorized",
            };

          return payload;
        }
        case 403: {
          const payload = error.response
            ? {
              status: 403,
              message: error.response.data.messsage || error.response,
            }
            : {
              status: 403,
              message: "User not Authorized",
            };

          return payload;
        }
        case 422:
        case 404:
          return error.response;
        case 400: {
          const payload = error.response
            ? {
              status: 400,
              message: error.response.data || error.response,
            }
            : {
              status: 400,
              message: "Bad Request",
            };
          return payload;
        }
        case 409:
          return { status: 409, message: error.response.data };

        case 500:
          return { status: 500, message: "Internal Server Error" };
        default:
          return error;
      }
    }
  );
});

export default axiosInstance;
